import { L3_BUCKET, getS3Object } from "../data/s3.js";
import { listL3Products } from "../decode/radarClient.js";
import { idbGet, idbSet } from "../cache/idb.js";
import { MAX_GAP_MS } from "./accumulate.js";

export const MAX_WINDOW_MS = 72 * 3600 * 1000;
export const DPR_ARCHIVE_START_MS = Date.UTC(2020, 3, 1);
const DOWNLOAD_CONCURRENCY = 6;
const RAW_CACHE_PREFIX = "rainraw:v1:";

let worker = null;
let nextId = 1;
const pending = new Map();

function getRainWorker() {
  if (!worker) {
    worker = new Worker(new URL("./rainWorker.js", import.meta.url), { type: "module" });
    worker.onmessage = (event) => {
      const { id, ok, result, error, progress } = event.data || {};
      const waiter = pending.get(id);
      if (!waiter) return;
      if (progress) {
        waiter.onProgress?.(progress);
        return;
      }
      pending.delete(id);
      if (ok) waiter.resolve(result);
      else waiter.reject(new Error(error));
    };
  }
  return worker;
}

function callRainWorker(type, payload, transfer = [], onProgress = null) {
  const id = nextId++;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject, onProgress });
    getRainWorker().postMessage({ id, type, payload }, transfer);
  });
}

function utcDateClean(ms) {
  return new Date(ms).toISOString().slice(0, 10).replace(/-/g, "");
}

function utcDaysBetween(startMs, endMs) {
  const days = [];
  for (let t = Math.floor(startMs / 86400000) * 86400000; t <= endMs; t += 86400000) days.push(utcDateClean(t));
  return days;
}

function keyTimeMs(dateClean, time) {
  const [h, m, s] = time.split(":").map(Number);
  return Date.UTC(+dateClean.slice(0, 4), +dateClean.slice(4, 6) - 1, +dateClean.slice(6, 8), h, m, s || 0);
}

// Every product inside the window, plus the nearest one on each side so the
// integration can cover the window edges.
export function selectBracketingProducts(products, startMs, endMs, maxGapMs = MAX_GAP_MS) {
  const sorted = [...products].sort((a, b) => a.tMs - b.tMs);
  const inside = sorted.filter((p) => p.tMs >= startMs && p.tMs <= endMs);
  const before = sorted.filter((p) => p.tMs < startMs && startMs - p.tMs <= maxGapMs).pop();
  const after = sorted.find((p) => p.tMs > endMs && p.tMs - endMs <= maxGapMs);
  return [before, ...inside, after].filter(Boolean);
}

async function listDprProducts(station, startMs, endMs, signal) {
  const products = [];
  for (const dateClean of utcDaysBetween(startMs - MAX_GAP_MS, endMs + MAX_GAP_MS)) {
    const listed = await listL3Products(station.id, dateClean, "DPR", { signal });
    for (const p of listed) products.push({ key: p.key, size: p.size, tMs: keyTimeMs(dateClean, p.time) });
  }
  return products;
}

async function fetchDprBytes(key, signal) {
  const cached = await idbGet(RAW_CACHE_PREFIX + key);
  if (cached?.value) return new Uint8Array(cached.value);
  const bytes = await getS3Object(L3_BUCKET, key, { signal });
  idbSet(RAW_CACHE_PREFIX + key, bytes.slice(), bytes.byteLength);
  return bytes;
}

// A window reaching the present ends at the newest scan, since the next one
// has not happened yet; counting those minutes as missing data would flag
// every recent window as a radar gap. If the newest scan is older than
// maxGapMs the radar really is down, so the window (and its gap) stand.
export function clampToLatestScan(products, endMs, nowMs = Date.now(), maxGapMs = MAX_GAP_MS) {
  if (!products.length || products.some((p) => p.tMs >= endMs)) return endMs;
  const latest = Math.max(...products.map((p) => p.tMs));
  return nowMs - latest <= maxGapMs ? latest : endMs;
}

export async function estimateRainRun({ station, startMs, endMs, signal }) {
  const products = selectBracketingProducts(await listDprProducts(station, startMs, endMs, signal), startMs, endMs);
  return { frames: products.length, bytes: products.reduce((n, p) => n + (p.size || 0), 0), products };
}

// Returns { grid, total (mm, Float32Array, NaN outside coverage), gaps,
// coveredMs, frameTimes, method ("advection" | "linear"), advectedPairs,
// linearPairs, startMs, endMs, source }.
export async function computeRainTotal({ station, startMs, endMs, signal, onProgress }) {
  if (!(endMs > startMs)) throw new Error("End time must be after start time");
  if (endMs - startMs > MAX_WINDOW_MS) throw new Error("Rain totals are limited to 72 hours");
  endMs = Math.min(endMs, Date.now());
  if (endMs < DPR_ARCHIVE_START_MS) {
    throw new Error("Rain totals need dual-pol rate data, which is archived from April 2020 onward");
  }

  onProgress?.({ phase: "listing", done: 0, total: 0 });
  const { products } = await estimateRainRun({ station, startMs, endMs, signal });
  if (products.length < 2) throw new Error("Not enough precipitation-rate scans in this window");
  endMs = clampToLatestScan(products, endMs);
  if (!(endMs > startMs)) throw new Error("No precipitation-rate scans yet in this window");

  const stationInfo = { id: station.id, lat: station.lat, lon: station.lon };
  await callRainWorker("reset");
  let done = 0;
  let cursor = 0;
  const pump = async () => {
    while (cursor < products.length) {
      const p = products[cursor++];
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const bytes = await fetchDprBytes(p.key, signal);
      await callRainWorker("addFrame", { bytes, station: stationInfo }, [bytes.buffer]);
      onProgress?.({ phase: "frames", done: ++done, total: products.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(DOWNLOAD_CONCURRENCY, products.length) }, pump));
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  onProgress?.({ phase: "integrating", done: products.length, total: products.length });
  const result = await callRainWorker("accumulate", { startMs, endMs }, [], onProgress);
  return { ...result, startMs, endMs, source: "DPR" };
}

// { rate, cum, observed, gaps, total } at the point, in mm/h and mm.
export function fetchRainSeries({ lat, lon }) {
  return callRainWorker("series", { lat, lon });
}

// [{ id, radarMm, gapMs }]: radar total over each gauge's [w0, w1] window.
export function compareGauges(gauges) {
  return callRainWorker("gaugeCompare", { gauges: gauges.map(({ id, lat, lon, w0, w1 }) => ({ id, lat, lon, w0, w1 })) });
}
